import type { Student } from '../types';

export type TeachTimerStatus = 'idle' | 'running' | 'paused' | 'ended';

export type TeachTimerState = {
  status: TeachTimerStatus;
  /** Chosen countdown duration (ms). */
  durationMs: number;
  /** Current remaining time (ms). */
  remainingMs: number;
  /** Wall-clock end timestamp when `status === 'running'`. */
  endsAtMs: number | null;
  /** Wall-clock time when last transitioned into running state. */
  startedAtMs: number | null;
};

export type TeachCohort = {
  courseId: string;
  courseName: string;
  assignmentId: string;
  assignmentTitle: string;
  students: Student[];
};

export type TeachLastRandomPick = {
  studentId: string;
  studentName: string;
  studentEmail?: string;
} | null;

export type TeachSessionState = {
  timer: TeachTimerState;
  cohort: TeachCohort | null;
  lastRandomPick: TeachLastRandomPick;
};

export const DEFAULT_TEACH_TIMER_MS = 5 * 60 * 1000;

export function makeIdleCountdown(durationMs: number, now = Date.now()): TeachTimerState {
  void now; // kept for symmetry; useful for future extensions
  return { status: 'idle', durationMs, remainingMs: durationMs, endsAtMs: null, startedAtMs: null };
}

export function startCountdown(durationMs: number, now = Date.now()): TeachTimerState {
  const nextEndsAt = now + Math.max(0, durationMs);
  return {
    status: 'running',
    durationMs,
    remainingMs: Math.max(0, durationMs),
    endsAtMs: nextEndsAt,
    startedAtMs: now,
  };
}

export function pauseCountdown(state: TeachTimerState, now = Date.now()): TeachTimerState {
  if (state.status !== 'running' || state.endsAtMs == null) return state;
  const remainingMs = Math.max(0, state.endsAtMs - now);
  return { ...state, status: remainingMs > 0 ? 'paused' : 'ended', remainingMs, endsAtMs: null };
}

export function tickCountdown(state: TeachTimerState, now = Date.now()): TeachTimerState {
  if (state.status !== 'running' || state.endsAtMs == null) return state;
  const remainingMs = Math.max(0, state.endsAtMs - now);
  if (remainingMs === 0) {
    return { ...state, status: 'ended', remainingMs, endsAtMs: null };
  }
  return { ...state, remainingMs };
}

export function resetCountdown(durationMs: number): TeachTimerState {
  return makeIdleCountdown(durationMs);
}

export function formatRemainingClock(ms: number): string {
  const clamped = Math.max(0, Math.floor(ms));
  const totalSeconds = Math.floor(clamped / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}
